<template>
  <form-dialog
    v-if="visible"
    :apply-button-disabled="!canSave"
    :apply-button-text="$t('Save')"
    height-size="fixed-auto"
    :model="model"
    :submit="save"
    :title="$t('Edit feature configuration')"
    :visible="visible"
    width-size="small"
    @close="close"
  >
    <div
      v-if="features.length"
      class="features-list"
    >
      <div
        v-for="feat in features"
        :key="feat.id"
        class="feature-item"
      >
        <div class="flex">
          <span class="left el-text--ellipsis">
            {{ feat.title }}
          </span>
          <span class="right">
            <el-switch v-model="model[feat.id]" />
          </span>
        </div>
        <div class="feature-description">
          {{ feat.description }}
        </div>
      </div>
    </div>
    <empty
      v-else
      icon="i-cyber-infrastructure-o--24"
      :title="$t('No available features')"
    />
    <template #body-footer>
      <el-alert
        :border="{top: true , bottom: false}"
        hide-close
        show-icon
        type="warning"
      >
        {{ $t('To use the feature configuration, contact the technical support. The listed features are available as Early Access functionalities and have limited support.') }}
      </el-alert>
    </template>
  </form-dialog>
</template>

<script>
  import { isEqual } from 'lodash-es';
  import FormDialog from '@/common/shared/FormDialog.vue';
  import Empty from '@/common/shared/Empty.vue';
  import { mapState } from 'pinia';
  import useFeaturesStore from '@/common/store/pinia/features';

  export default {
    name: 'FeaturesConfig',
    components: { FormDialog, Empty },
    data() {
      return {
        visible: false,
        model: {}
      };
    },
    mounted() {
      document.addEventListener('keydown', this.keyListener.bind(this));
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.keyListener.bind(this));
    },
    computed: {
      ...mapState(useFeaturesStore, ['features']),
      enabledFeatures() {
        try {
          return JSON.parse(window.localStorage.getItem('features')) || {};
        } catch {
          return {};
        }
      },
      updatedValues() {
        return this.features.reduce((result, feat) => {
          if (this.model[feat.id] !== feat.enabled) {
            result[feat.id] = this.model[feat.id];
          }
          return result;
        }, {});
      },
      canSave() {
        return !isEqual(this.updatedValues, this.enabledFeatures);
      }
    },
    watch: {
      visible(newVal) {
        if (newVal) {
          this.initForm();
        }
      }
    },
    methods: {
      close() {
        this.visible = false;
      },
      save() {
        if (!this.canSave) {
          return;
        }

        this.setEnabledFeatures(this.updatedValues);
        this.close();
        window.location.reload();
      },
      setEnabledFeatures(enabledFeatures) {
        window.localStorage.setItem('features', JSON.stringify(enabledFeatures));
      },
      keyListener(e) {
        if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          this.visible = true;
        }
      },
      initForm() {
        this.features.forEach((feat) => {
          this.$set(
            this.model,
            feat.id,
            typeof this.enabledFeatures[feat.id] === 'boolean'
              ? this.enabledFeatures[feat.id]
              : feat.enabled
          );
        });
      }
    }
  };
</script>

<style scoped lang="scss">
  .feature-item {
    padding: 0 24px;
    margin: 16px 0;

    &:not(:last-child) {
      padding-bottom: 16px;
      border-bottom: 1px solid $av-brand-lighter;
    }
  }

  .feature-description {
    color: $av-fixed-light;
  }

  .flex {
    display: flex;
    width: 100%;
    max-width: 100%;
    align-items: center;

    .left {
      flex: 1 1 auto;
    }

    .right {
      flex: 0 1 auto;
    }
  }
</style>
